import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext';

const API_URL = '/api/admin/loans/';
const STAFF_URL = '/api/admin/staff/';

const formatMoney = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeMoneyInput = (value) => {
    const cleaned = String(value ?? '').replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) return cleaned;
    return `${parts[0]}.${parts.slice(1).join('')}`;
};

const stripMoneyFormatting = (value) => String(value ?? '').replace(/,/g, '');

const Loans = () => {
    const { hasPermission } = usePermissions();
    const [loans, setLoans] = useState([]);
    const [activeTab, setActiveTab] = useState('active'); // active, completed
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [modalMode, setModalMode] = useState('create'); // create | edit | view
    
    // Form State
    const [staffOptions, setStaffOptions] = useState([]);
    const [formData, setFormData] = useState({
        staff: '',
        amount: '',
        monthly_deduction: '',
        start_month: new Date().getMonth() + 1,
        start_year: new Date().getFullYear()
    });

    const canView = hasPermission('LOANS', 'can_view');
    const canCreate = hasPermission('LOANS', 'can_create');
    const canEdit = hasPermission('LOANS', 'can_edit');
    const canDelete = hasPermission('LOANS', 'can_delete');

    useEffect(() => {
        fetchLoans();
        fetchStaff();
    }, []);

    const fetchLoans = async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch loans');
            const data = await res.json();
            setLoans(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStaff = async () => {
        try {
            const res = await fetch(`${STAFF_URL}?page_size=1000`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                const staff = data.results || data;
                setStaffOptions(staff.map(s => ({ id: s.id, text: `${s.first_name} ${s.last_name} (${s.nida})` })));
            }
        } catch (e) { console.error(e); }
    };

    const resetForm = () => {
        setFormData({
            staff: '',
            amount: '',
            monthly_deduction: '',
            start_month: new Date().getMonth() + 1,
            start_year: new Date().getFullYear()
        });
        setEditingId(null);
        setModalMode('create');
    };

    const openCreate = () => {
        resetForm();
        setModalMode('create');
        setShowModal(true);
    };

    const openEdit = (loan) => {
        setEditingId(loan.id);
        setModalMode('edit');
        setFormData({
            staff: loan.staff,
            amount: formatMoney(loan.amount || 0),
            monthly_deduction: formatMoney(loan.monthly_deduction || 0),
            start_month: loan.start_month,
            start_year: loan.start_year,
        });
        setShowModal(true);
    };

    const openView = (loan) => {
        setEditingId(loan.id);
        setModalMode('view');
        setFormData({
            staff: loan.staff,
            amount: formatMoney(loan.amount || 0),
            monthly_deduction: formatMoney(loan.monthly_deduction || 0),
            start_month: loan.start_month,
            start_year: loan.start_year,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const endpoint = editingId ? `${API_URL}${editingId}/` : API_URL;
            const method = editingId ? 'PATCH' : 'POST';
            const res = await fetch(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({
                    ...formData,
                    amount: stripMoneyFormatting(formData.amount),
                    monthly_deduction: stripMoneyFormatting(formData.monthly_deduction),
                })
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.detail || 'Failed to create loan');
            }
            toast.success(editingId ? 'Loan updated successfully' : 'Loan created successfully');
            setShowModal(false);
            fetchLoans();
            resetForm();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (loanId) => {
        if (!window.confirm('Delete this loan record?')) return;
        try {
            const res = await fetch(`${API_URL}${loanId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete loan');
            toast.success('Loan deleted successfully');
            fetchLoans();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const isReadOnlyMode = modalMode === 'view';

    const filteredLoans = loans.filter(l => 
        activeTab === 'active' ? l.status === 'ACTIVE' : l.status === 'COMPLETED'
    );

    const { items: sortedFilteredLoans, requestSort, getSortIndicator } = useSortableTable(filteredLoans);

    const MONTH_OPTIONS = [
        { id: 1, text: 'January' }, { id: 2, text: 'February' }, { id: 3, text: 'March' },
        { id: 4, text: 'April' }, { id: 5, text: 'May' }, { id: 6, text: 'June' },
        { id: 7, text: 'July' }, { id: 8, text: 'August' }, { id: 9, text: 'September' },
        { id: 10, text: 'October' }, { id: 11, text: 'November' }, { id: 12, text: 'December' }
    ];

    const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
        const y = new Date().getFullYear() + i;
        return { id: y, text: y.toString() };
    });

    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>Manage Loans</h2>
                <button className="btn btn-primary" disabled={!canCreate} onClick={openCreate}>
                    <i className="fa fa-plus"></i> New Loan
                </button>
            </div>

            <div className="custom-tabs-container" style={{ marginTop: 16 }}>
                <button className={`custom-tab ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>Active Loans</button>
                <button className={`custom-tab ${activeTab === 'completed' ? 'active' : ''}`} onClick={() => setActiveTab('completed')}>History</button>
            </div>

            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('staff_name')} style={{ cursor: 'pointer' }}>Staff {getSortIndicator('staff_name')}</th>
                            <th onClick={() => requestSort('amount')} style={{ cursor: 'pointer' }}>Total Amount {getSortIndicator('amount')}</th>
                            <th onClick={() => requestSort('monthly_deduction')} style={{ cursor: 'pointer' }}>Monthly Deduction {getSortIndicator('monthly_deduction')}</th>
                            <th onClick={() => requestSort('balance')} style={{ cursor: 'pointer' }}>Remaining Balance {getSortIndicator('balance')}</th>
                            <th onClick={() => requestSort('start_year')} style={{ cursor: 'pointer' }}>Start Date {getSortIndicator('start_year')}</th>
                            <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status {getSortIndicator('status')}</th>
                            {(canView || canEdit || canDelete) && <th>Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLoans.length === 0 && <tr><td colSpan={(canView || canEdit || canDelete) ? 7 : 6} className="no-data">No loans found.</td></tr>}
                        {sortedFilteredLoans.map(loan => (
                            <tr key={loan.id}>
                                <td>{loan.staff_name || loan.staff_details?.name || 'Staff ' + loan.staff}</td>
                                <td>{formatMoney(loan.amount)}</td>
                                <td>{formatMoney(loan.monthly_deduction)}</td>
                                <td>{formatMoney(loan.balance || 0)}</td>
                                <td>{loan.start_month}/{loan.start_year}</td>
                                <td>
                                    <span className={`badge badge-${loan.status === 'ACTIVE' ? 'success' : 'secondary'}`}>
                                        {loan.status}
                                    </span>
                                </td>
                                {(canView || canEdit || canDelete) && (
                                    <td className="actions">
                                        {canView && (
                                            <button className="icon-btn view-btn" onClick={() => openView(loan)} title="View">
                                                <i className="fa fa-eye" />
                                            </button>
                                        )}
                                        {canEdit && (
                                            <button className="icon-btn edit-btn" onClick={() => openEdit(loan)} title="Edit">
                                                <i className="fa fa-pencil" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button className="icon-btn delete-btn" onClick={() => handleDelete(loan.id)} title="Delete">
                                                <i className="fa fa-trash" />
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px', padding: 0, overflow: 'hidden' }}>
                        <button className="modal-close-btn" onClick={() => setShowModal(false)} style={{ zIndex: 10 }}>&times;</button>
                        
                        <div className="dialog-container" style={{ boxShadow: 'none', border: 'none', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="dialog-header">
                                <h3>{modalMode === 'edit' ? 'Edit Loan' : modalMode === 'view' ? 'View Loan' : 'Add New Loan'}</h3>
                            </div>
                            
                            <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
                                <form id="loan-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="required">Staff</label>
                                        {isReadOnlyMode ? (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={staffOptions.find(s => String(s.id) === String(formData.staff))?.text || ''}
                                                readOnly
                                            />
                                        ) : (
                                            <Select2Dropdown
                                                options={staffOptions}
                                                value={formData.staff}
                                                onChange={(val) => setFormData({...formData, staff: val})}
                                                displayKey="text"
                                                valueKey="id"
                                            />
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Loan Amount</label>
                                        <input 
                                            type="text"
                                            className="form-control" 
                                            value={formData.amount || ''}
                                            onChange={e => setFormData({...formData, amount: normalizeMoneyInput(e.target.value)})}
                                            onFocus={e => setFormData({...formData, amount: stripMoneyFormatting(e.target.value)})}
                                            onBlur={e => setFormData({...formData, amount: formatMoney(e.target.value || 0)})}
                                            readOnly={isReadOnlyMode}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Monthly Deduction</label>
                                        <input 
                                            type="text"
                                            className="form-control" 
                                            value={formData.monthly_deduction || ''}
                                            onChange={e => setFormData({...formData, monthly_deduction: normalizeMoneyInput(e.target.value)})}
                                            onFocus={e => setFormData({...formData, monthly_deduction: stripMoneyFormatting(e.target.value)})}
                                            onBlur={e => setFormData({...formData, monthly_deduction: formatMoney(e.target.value || 0)})}
                                            readOnly={isReadOnlyMode}
                                            required
                                        />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label className="required">Start Month</label>
                                            {isReadOnlyMode ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={MONTH_OPTIONS.find(m => String(m.id) === String(formData.start_month))?.text || ''}
                                                    readOnly
                                                />
                                            ) : (
                                                <Select2Dropdown
                                                    options={MONTH_OPTIONS}
                                                    value={formData.start_month}
                                                    onChange={(val) => setFormData({...formData, start_month: val})}
                                                    displayKey="text"
                                                    valueKey="id"
                                                />
                                            )}
                                        </div>
                                        <div className="col-md-6 form-group">
                                            <label className="required">Start Year</label>
                                            {isReadOnlyMode ? (
                                                <input type="text" className="form-control" value={formData.start_year || ''} readOnly />
                                            ) : (
                                                <Select2Dropdown
                                                    options={YEAR_OPTIONS}
                                                    value={formData.start_year}
                                                    onChange={(val) => setFormData({...formData, start_year: val})}
                                                    displayKey="text"
                                                    valueKey="id"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="dialog-footer">
                                {!isReadOnlyMode && (
                                    <button 
                                        type="submit" 
                                        form="loan-form"
                                        className="dialog-btn dialog-btn-primary" 
                                    >
                                        {editingId ? 'Update Loan' : 'Create Loan'}
                                    </button>
                                )}
                                <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Loans;
